import { useMemo, useState } from 'react';
import { Database, Download, BarChart3 } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBootstrapJobs } from '@/hooks/use-bootstrap-jobs';
import { DownloadDialog } from '@/components/bootstrap/download-dialog';
import { formatDateTime } from '@/lib/utils/format';
import type { BootstrapJob } from '@/lib/types/bootstrap';

const providers: Record<string, { label: string; color: string }> = {
  jira: { label: 'Jira', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  atlassian: { label: 'Atlassian', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  freshservice: { label: 'Freshservice', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' },
  solarwinds: { label: 'SolarWinds', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  servicenow: { label: 'ServiceNow', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  zendesk: { label: 'Zendesk', color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200' },
  freshdesk: { label: 'Freshdesk', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' },
};

const defaultColor = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';

const METABASE_DASHBOARD_URL = 'https://metabase.harmony.io/dashboard/11-historic-classifications-overview';

const metabaseProviderMap: Record<string, string> = {
  atlassian: 'jira',
};

function buildDashboardUrl(tenantId: string, provider: string) {
  // eslint-disable-next-line security/detect-object-injection
  const metabaseProvider = metabaseProviderMap[provider] ?? provider;
  const params = new URLSearchParams({ tenant_id: tenantId, provider: metabaseProvider });
  return `${METABASE_DASHBOARD_URL}?${params.toString()}`;
}

function getProvider(key: string) {
  // eslint-disable-next-line security/detect-object-injection
  const match = providers[key] as { label: string; color: string } | undefined;
  return match ?? { label: key.charAt(0).toUpperCase() + key.slice(1), color: defaultColor };
}

function groupByIntegration(jobs: BootstrapJob[]) {
  const groups = new Map<string, BootstrapJob[]>();
  for (const job of jobs) {
    const group = groups.get(job.integration_id) ?? [];
    group.push(job);
    groups.set(job.integration_id, group);
  }

  const summaries: BootstrapJob[] = [];
  for (const group of groups.values()) {
    summaries.push(group.find((j) => j.status === 'completed') ?? group[0]);
  }
  return summaries;
}

interface ItsmDataSectionProps {
  tenantId: string;
}

export function ItsmDataSection({ tenantId }: ItsmDataSectionProps) {
  const [downloadJobId, setDownloadJobId] = useState<string | null>(null);
  const { data, isLoading } = useBootstrapJobs({ tenant_id: tenantId });

  const integrations = useMemo(
    () => groupByIntegration(data?.jobs ?? []),
    [data?.jobs],
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            ITSM Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-8 w-full" />
          ) : integrations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No data</p>
          ) : (
            <div className="grid gap-3">
              {integrations.map((job) => {
                const canDownload = job.status === 'completed' && !!job.s3_prefix;
                const ticketCount = job.total_tickets ?? job.processed_tickets;

                const provider = getProvider(job.provider);

                return (
                  <div key={job.integration_id} className="flex items-center justify-between">
                    <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${provider.color}`}>
                      {provider.label}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground text-sm tabular-nums">
                        {ticketCount.toLocaleString()} tickets
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {formatDateTime(job.completed_at ?? job.created_at)}
                      </span>
                      <a
                        href={buildDashboardUrl(tenantId, job.provider)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View dashboard"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        <BarChart3 className="h-3.5 w-3.5" />
                      </a>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={!canDownload}
                        onClick={() => { setDownloadJobId(job.id); }}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <DownloadDialog
        jobId={downloadJobId}
        open={downloadJobId !== null}
        onOpenChange={(open) => {
          if (!open) setDownloadJobId(null);
        }}
      />
    </>
  );
}
